/**
 * Sentry Error Monitoring Configuration (Phase 12c)
 *
 * Initialize Sentry for centralized error tracking and monitoring.
 * Set VITE_SENTRY_DSN environment variable to enable.
 */
import * as Sentry from '@sentry/react'

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN

export function initSentry() {
  if (!SENTRY_DSN) {
    console.log('[Sentry] DSN not configured - error monitoring disabled')
    return
  }

  Sentry.init({
    dsn: SENTRY_DSN,

    // Environment configuration
    environment: import.meta.env.MODE, // 'development' or 'production'

    // Release tracking (set via build process)
    release: import.meta.env.VITE_APP_VERSION || 'development',

    // Sample rates
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,

    // Only send errors in production, unless explicitly enabled
    enabled: import.meta.env.MODE === 'production' || import.meta.env.VITE_SENTRY_ENABLED === 'true',

    // Integrations
    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),
      // Replay for session recording on errors
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Replay sample rates
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Filter out noisy errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'fb_xd_fragment',
      // Network errors that are expected
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      // User cancellations
      'AbortError',
      'The operation was aborted',
    ],

    // Don't send PII
    sendDefaultPii: false,

    // Before sending, add context
    beforeSend(event, hint) {
      // Don't send in development unless explicitly enabled
      if (import.meta.env.MODE === 'development' && import.meta.env.VITE_SENTRY_ENABLED !== 'true') {
        console.log('[Sentry] Would have sent:', event)
        return null
      }

      // Add additional context
      if (event.exception) {
        event.tags = {
          ...event.tags,
          errorSource: 'frontend',
        }
      }

      return event
    },
  })

  console.log('[Sentry] Initialized for', import.meta.env.MODE)
}

// Export Sentry for manual error reporting
export { Sentry }

/**
 * Utility to capture a custom error with context
 */
export function captureError(error: Error, context?: Record<string, unknown>) {
  if (SENTRY_DSN) {
    Sentry.captureException(error, {
      extra: context,
    })
  }
  console.error('[Error]', error, context)
}

/**
 * Utility to add breadcrumb for debugging
 */
export function addBreadcrumb(message: string, category: string, data?: Record<string, unknown>) {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  })
}

/**
 * Set user context for error tracking
 */
export function setUser(userId: string | null, email?: string) {
  if (userId) {
    Sentry.setUser({ id: userId, email })
  } else {
    Sentry.setUser(null)
  }
}
